import { supabase } from "../../supabaseClient";

const cleanCode = (value) => String(value || "").trim().toUpperCase();

export async function getGameRoomOverview() {
  const [leaderboardsResult, scoreboardResult] = await Promise.all([
    supabase.rpc("get_game_room_leaderboards"),
    supabase.rpc("get_my_game_scoreboard"),
  ]);

  if (leaderboardsResult.error) throw leaderboardsResult.error;

  // Anonymous users are expected to receive an auth error for the personal board.
  // Treat that as an empty personal board while still showing public leaders.
  const personal = scoreboardResult.error ? [] : scoreboardResult.data || [];

  return {
    leaderboards: leaderboardsResult.data || [],
    personal,
  };
}

export async function createGameSession({
  gameKey,
  maxPlayers = 2,
  initialState = {},
}) {
  const { data, error } = await supabase.rpc("game_create_session", {
    p_game_key: gameKey,
    p_max_players: maxPlayers,
    p_initial_state: initialState,
  });

  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function joinGameSession(joinCode) {
  const { data, error } = await supabase.rpc("game_join_session", {
    p_join_code: cleanCode(joinCode),
  });

  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function joinBlackjackSession(joinCode) {
  const { data, error } = await supabase.rpc("game_join_blackjack_session", {
    p_join_code: cleanCode(joinCode),
  });

  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function setGamePlayerReady(sessionId, ready) {
  const { data, error } = await supabase.rpc("game_set_ready", {
    p_session_id: sessionId,
    p_ready: !!ready,
  });

  if (error) throw error;
  return data;
}

export async function leaveGameSession(sessionId) {
  const { data, error } = await supabase.rpc("game_leave_session", {
    p_session_id: sessionId,
  });

  if (error) throw error;
  return data;
}

export async function getGameSession(sessionId) {
  const [{ data: session, error: sessionError }, { data: players, error: playersError }] =
    await Promise.all([
      supabase
        .from("game_sessions")
        .select("*")
        .eq("id", sessionId)
        .single(),
      supabase
        .from("game_session_players")
        .select("session_id,user_id,seat,ready,joined_at,left_at")
        .eq("session_id", sessionId)
        .order("seat", { ascending: true }),
    ]);

  if (sessionError) throw sessionError;
  if (playersError) throw playersError;

  return { session, players: players || [] };
}

export async function appendGameAction({
  sessionId,
  actionType,
  payload = {},
  expectedVersion = null,
}) {
  const { data, error } = await supabase.rpc("game_append_action", {
    p_session_id: sessionId,
    p_action_type: actionType,
    p_payload: payload,
    p_expected_version: expectedVersion,
  });

  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function submitSoloGameResult({
  gameKey,
  result = "completed",
  score = null,
  durationMs = null,
  stats = {},
}) {
  const { data, error } = await supabase.rpc("game_submit_solo_result", {
    p_game_key: gameKey,
    p_result: result,
    p_score: score,
    p_duration_ms: durationMs,
    p_stats: stats,
  });

  if (error) throw error;
  return data;
}

export async function resetPersonalGameSeason(gameKey) {
  const { data, error } = await supabase.rpc("game_reset_personal_season", {
    p_game_key: gameKey,
  });

  if (error) throw error;
  return data;
}

export function subscribeToGameSession(
  sessionId,
  { onSessionChange, onPlayersChange, onAction, onReaction } = {}
) {
  const channel = supabase
    .channel(`game-session:${sessionId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "game_sessions",
        filter: `id=eq.${sessionId}`,
      },
      (payload) => onSessionChange?.(payload)
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "game_session_players",
        filter: `session_id=eq.${sessionId}`,
      },
      (payload) => onPlayersChange?.(payload)
    )
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "game_actions",
        filter: `session_id=eq.${sessionId}`,
      },
      (payload) => onAction?.(payload)
    )
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "game_blackjack_reactions",
        filter: `session_id=eq.${sessionId}`,
      },
      (payload) => onReaction?.(payload)
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export async function getGameSessionPlayers(sessionId) {
  const { data, error } = await supabase.rpc("game_get_session_players", {
    p_session_id: sessionId,
  });

  if (error) throw error;
  return data || [];
}

export async function submitChessMove({ sessionId, snapshot, expectedVersion }) {
  const { data, error } = await supabase.rpc("game_chess_move", {
    p_session_id: sessionId,
    p_snapshot: snapshot,
    p_expected_version: expectedVersion,
  });

  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function resignChessGame(sessionId) {
  const { data, error } = await supabase.rpc("game_chess_resign", {
    p_session_id: sessionId,
  });

  if (error) throw error;
  return data;
}

export async function getMyOpenGameSession(gameKey) {
  const { data, error } = await supabase.rpc("game_get_my_open_session", {
    p_game_key: gameKey,
  });

  if (error) throw error;
  return data || null;
}

// ============================================================
// ONLINE BLACKJACK
// ============================================================

export async function createBlackjackSession({ maxPlayers = 4, visibility = "invite" } = {}) {
  const { data, error } = await supabase.rpc("game_create_blackjack_session", {
    p_max_players: maxPlayers,
    p_visibility: visibility,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function joinPublicBlackjackSession() {
  const { data, error } = await supabase.rpc("game_join_public_blackjack");
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function setBlackjackPlayerReady(sessionId, ready) {
  const { data, error } = await supabase.rpc("game_blackjack_set_ready", {
    p_session_id: sessionId,
    p_ready: !!ready,
  });
  if (error) throw error;
  return data;
}

export async function startBlackjackSession(sessionId) {
  const { data, error } = await supabase.rpc("game_blackjack_start", {
    p_session_id: sessionId,
  });
  if (error) throw error;
  return data;
}

export async function placeBlackjackBet({ sessionId, amount, expectedVersion }) {
  const { data, error } = await supabase.rpc("game_blackjack_bet", {
    p_session_id: sessionId,
    p_amount: amount,
    p_expected_version: expectedVersion,
  });
  if (error) throw error;
  return data;
}

export async function submitBlackjackAction({ sessionId, action, expectedVersion }) {
  const { data, error } = await supabase.rpc("game_blackjack_action", {
    p_session_id: sessionId,
    p_action: action,
    p_expected_version: expectedVersion,
  });
  if (error) throw error;
  return data;
}

export async function startNextBlackjackRound({ sessionId, expectedVersion }) {
  const { data, error } = await supabase.rpc("game_blackjack_next_round", {
    p_session_id: sessionId,
    p_expected_version: expectedVersion,
  });
  if (error) throw error;
  return data;
}

export async function submitBlackjackInsurance({
  sessionId,
  take,
  expectedVersion,
}) {
  const { data, error } = await supabase.rpc("game_blackjack_insurance", {
    p_session_id: sessionId,
    p_take: !!take,
    p_expected_version: expectedVersion,
  });

  if (error) throw error;
  return data;
}


export async function leaveBlackjackSession(sessionId) {
  const { data, error } = await supabase.rpc("game_blackjack_leave", {
    p_session_id: sessionId,
  });

  if (error) throw error;
  return data;
}

async function tickBlackjackSessionAnonymously(sessionId) {
  const supabaseUrl = supabase?.supabaseUrl;
  const supabaseKey = supabase?.supabaseKey;

  if (!supabaseUrl || !supabaseKey) {
    throw new Error("Blackjack deadline fallback is unavailable.");
  }

  const response = await fetch(
    `${supabaseUrl}/rest/v1/rpc/game_blackjack_tick`,
    {
      method: "POST",
      headers: {
        apikey: supabaseKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ p_session_id: sessionId }),
    }
  );

  if (!response.ok) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (_) {
      // Ignore JSON parsing failure and use the HTTP status below.
    }

    const error = new Error(
      payload?.message ||
        payload?.error_description ||
        `Blackjack deadline fallback failed (${response.status}).`
    );
    error.code = payload?.code || response.status;
    throw error;
  }

  return response.json();
}

export async function tickBlackjackSession(sessionId) {
  const { data, error } = await supabase.rpc("game_blackjack_tick", {
    p_session_id: sessionId,
  });

  if (!error) return data;

  // A browser can still have the table mounted after its stored refresh token
  // has become invalid. Deadline advancement is deliberately safe for anon
  // callers, so retry this one narrowly scoped RPC without the user's stale JWT.
  const text = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const authLikeError =
    text.includes("refresh token") ||
    text.includes("jwt") ||
    text.includes("not authenticated") ||
    text.includes("authentication") ||
    String(error?.code || "").startsWith("401") ||
    String(error?.code || "").startsWith("403");

  if (authLikeError) {
    return tickBlackjackSessionAnonymously(sessionId);
  }

  throw error;
}

export async function sitOutBlackjackHand({ sessionId, expectedVersion }) {
  const { data, error } = await supabase.rpc("game_blackjack_sit_out", {
    p_session_id: sessionId,
    p_expected_version: expectedVersion,
  });

  if (error) throw error;
  return data;
}

export async function resumeBlackjackBetting({ sessionId, expectedVersion = null }) {
  const { data, error } = await supabase.rpc("game_blackjack_resume_betting", {
    p_session_id: sessionId,
    p_expected_version: expectedVersion,
  });

  if (error) throw error;
  return data;
}

export async function sendBlackjackReaction({ sessionId, reactionKey }) {
  const { data, error } = await supabase.rpc("game_blackjack_react", {
    p_session_id: sessionId,
    p_reaction_key: reactionKey,
  });

  if (error) throw error;
  return data;
}

export function subscribeToGamePresence({ sessionId, userId, onSync }) {
  if (!sessionId || !userId) return () => {};

  const channel = supabase.channel(`game-presence:${sessionId}`, {
    config: {
      presence: {
        key: userId,
      },
    },
  });

  const publishPresence = () => {
    const state = channel.presenceState();
    const userIds = Object.entries(state)
      .filter(([, entries]) => Array.isArray(entries) && entries.length > 0)
      .map(([key]) => key);

    onSync?.(userIds, state);
  };

  channel
    .on("presence", { event: "sync" }, publishPresence)
    .subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        try {
          await channel.track({
            user_id: userId,
            online_at: new Date().toISOString(),
          });
        } catch (error) {
          console.warn("Game presence track failed:", error);
        }
      }
    });

  return () => {
    try {
      channel.untrack();
    } catch {
      // Best-effort presence cleanup; removeChannel handles disconnect too.
    }
    supabase.removeChannel(channel);
  };
}


export async function getBlackjackWallet() {
  const { data, error } = await supabase.rpc("game_blackjack_get_wallet");
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function claimBlackjackRefill(sessionId = null) {
  const { data, error } = await supabase.rpc("game_blackjack_claim_refill", {
    p_session_id: sessionId,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function syncSoloBlackjackWallet(balance) {
  const { data, error } = await supabase.rpc("game_blackjack_sync_solo_wallet", {
    p_balance: Number(balance || 0),
  });
  if (error) throw error;
  return Number(data || 0);
}
